import { useRef, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PORTFOLIO_ITEMS, PILLAR_CATEGORIES } from '../data/portfolioData';

const CALENDLY_URL = 'https://calendly.com/jagan-motionx/30min';

/* ── Widescreen 16:9 Editorial Card (Supports Lead 2-Col Feature) ── */
function WidescreenEditorialCard({ item, index, isFeaturedSpan, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const vidRef = useRef(null);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (hovered || isFeaturedSpan) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [hovered, isFeaturedSpan]);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(item)}
      className={`group cursor-pointer flex flex-col transition-opacity duration-300 ${
        isFeaturedSpan ? 'lg:col-span-2' : ''
      }`}
    >
      <div
        className={`relative aspect-video rounded-xl overflow-hidden bg-[#0e0e11] border border-white/[0.08] group-hover:border-white/30 transition-all duration-500 flex items-center justify-center`}
      >
        <img
          src={item.posterUrl}
          alt={`${item.title} — ${item.subtitle}`}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 ${
            hovered || isFeaturedSpan ? 'scale-[1.02] opacity-0' : 'scale-100 opacity-100'
          }`}
        />

        {(hovered || isFeaturedSpan) && (
          <video
            ref={vidRef}
            src={item.videoUrl}
            poster={item.posterUrl}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Top-Left Index & Category Badge */}
        <div className="absolute top-3.5 left-3.5 z-20 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded bg-black/70 backdrop-blur-md text-white/60 text-[10px] font-mono">
            {String(index + 1).padStart(2, '0')}
          </span>
          {item.highlight ? (
            <span className="px-2.5 py-1 rounded bg-[#E50914] text-white text-[10px] font-medium tracking-[0.04em]">
              ★ Featured Flagship
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded bg-black/70 backdrop-blur-md text-white/90 text-[11px]">
              {item.subType}
            </span>
          )}
        </div>

        {/* Bottom-Right Interactive Play Lens */}
        <div className="absolute bottom-3.5 right-3.5 z-20 flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-full bg-black/75 backdrop-blur-md border border-white/15 text-white text-[11px] font-medium flex items-center gap-1.5 group-hover:bg-[#E50914] group-hover:text-white transition-colors">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 20,12 5,21" />
            </svg>
            {isFeaturedSpan ? 'Play with Sound' : 'Watch'}
          </span>
        </div>
      </div>

      {/* Editorial Caption */}
      <div className="pt-3.5 flex flex-col sm:flex-row sm:items-baseline justify-between gap-1.5 sm:gap-4">
        <div className="min-w-0">
          <h3
            className={`${
              isFeaturedSpan
                ? 'text-[clamp(1.1rem,1.5vw,1.35rem)]'
                : 'text-[clamp(1rem,1.2vw,1.1rem)]'
            } font-medium tracking-[-0.02em] text-white group-hover:text-white/80 transition-colors leading-snug`}
          >
            {item.title}
          </h3>
          <p className="text-[12px] sm:text-[13px] text-[#767d88] mt-0.5 leading-snug">
            {item.client} &nbsp;·&nbsp; {item.subtitle}
          </p>
        </div>
        <span className="text-[11px] font-mono text-white/45 shrink-0">
          {item.productionMode}
        </span>
      </div>
    </motion.article>
  );
}

/* ── Native 9:16 Vertical Cinema Card (For Micro-Dramas & Vertical Brand Ads) ── */
function VerticalReelCard({ item, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const vidRef = useRef(null);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (hovered) {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [hovered]);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(item)}
      className="group cursor-pointer flex flex-col"
    >
      <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-[#0e0e11] border border-white/[0.08] group-hover:border-white/35 transition-all duration-500">
        <img
          src={item.posterUrl}
          alt={item.title}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 ${
            hovered ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
          }`}
        />

        {hovered && (
          <video
            ref={vidRef}
            src={item.videoUrl}
            poster={item.posterUrl}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent pointer-events-none" />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 right-3 z-20 flex items-center justify-between gap-1">
          <span className="px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-[10px] font-medium">
            {item.client}
          </span>
          <span className="px-2 py-0.5 rounded bg-black/70 backdrop-blur-md text-white/75 text-[10px] font-mono">
            9:16
          </span>
        </div>

        {/* Bottom Caption Inside Portrait Frame */}
        <div className="absolute bottom-4 left-3.5 right-3.5 z-20">
          <p className="text-[10px] font-mono uppercase tracking-[0.08em] text-white/70 mb-1">
            {item.subType}
          </p>
          <h4 className="text-[clamp(0.88rem,1.05vw,1rem)] font-medium tracking-[-0.02em] text-white leading-snug">
            {item.title}
          </h4>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-white/80 mt-1.5 group-hover:text-white">
            ▶ Watch Reel
          </span>
        </div>
      </div>
    </motion.article>
  );
}

export default function FeaturedWork({ initialCategory = 'brand_work' }) {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [brandSubFilter, setBrandSubFilter] = useState('all_brand');
  const [theaterItem, setTheaterItem] = useState(null);

  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);

  const { horizontalItems, verticalItems } = useMemo(() => {
    let base = PORTFOLIO_ITEMS;
    if (activeCategory === 'vertical') {
      base = PORTFOLIO_ITEMS.filter((item) => item.aspect === '9:16');
    } else if (activeCategory !== 'all') {
      base = PORTFOLIO_ITEMS.filter((item) => item.category === activeCategory);
    }

    if (activeCategory === 'brand_work' && brandSubFilter !== 'all_brand') {
      if (brandSubFilter === 'commercials') {
        base = base.filter((x) => x.subType === 'Brand Commercial');
      } else if (brandSubFilter === 'micro_dramas') {
        base = base.filter((x) => x.subType.includes('Micro-Drama'));
      }
    }

    return {
      horizontalItems: base.filter((x) => x.aspect !== '9:16'),
      verticalItems: base.filter((x) => x.aspect === '9:16'),
    };
  }, [activeCategory, brandSubFilter]);

  const activeCategoryMeta = useMemo(() => {
    return (
      PILLAR_CATEGORIES.find((c) => c.id === activeCategory) || PILLAR_CATEGORIES[0]
    );
  }, [activeCategory]);

  return (
    <section id="featured-work" className="bg-black py-20 sm:py-28 w-full">
      <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24">
        {/* Editorial Section Header */}
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 pb-8 border-b border-white/[0.08]">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] mb-2">
              ● Curated Portfolio &nbsp;·&nbsp; Hover Any Frame to Preview
            </p>
            <h2 className="text-white font-normal tracking-[-0.03em] leading-[1.05] text-[clamp(1.85rem,3.4vw,3.25rem)]">
              {activeCategoryMeta.label}
            </h2>
            <p className="text-[#767d88] text-sm sm:text-[15px] mt-2 max-w-2xl">
              {activeCategoryMeta.summary}
            </p>
          </div>

          {/* Minimal Pill Category Switcher */}
          <div className="flex flex-wrap items-center gap-2">
            {PILLAR_CATEGORIES.map((cat) => {
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setBrandSubFilter('all_brand');
                  }}
                  className={`px-4 py-2 rounded-full text-[13px] tracking-[-0.01em] transition-all flex items-center gap-2 cursor-pointer ${
                    active
                      ? 'bg-[#E50914] text-white font-medium shadow-[0_0_20px_rgba(229,9,20,0.3)]'
                      : 'bg-white/[0.05] text-white/70 hover:bg-white/[0.1] hover:text-white'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[11px] ${active ? 'text-white/80' : 'text-white/40'}`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-Filter Bar when viewing Brand Work */}
        {activeCategory === 'brand_work' && (
          <div className="flex flex-wrap items-center gap-2 pt-5">
            {[
              { id: 'all_brand', label: 'All Client Work (9)' },
              {
                id: 'commercials',
                label: 'Brand Commercials — BKIC, Alcobrew BHRUM, Seef, XPENG (5)',
              },
              {
                id: 'micro_dramas',
                label: '9:16 Client Micro-Dramas — DramaBox & Dashverse (4)',
              },
            ].map((sub) => (
              <button
                key={sub.id}
                type="button"
                onClick={() => setBrandSubFilter(sub.id)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] cursor-pointer transition-all border ${
                  brandSubFilter === sub.id
                    ? 'border-white/40 bg-white/10 text-white font-medium'
                    : 'border-transparent bg-transparent text-[#767d88] hover:text-white'
                }`}
              >
                {sub.label}
              </button>
            ))}
          </div>
        )}

        {/* 1. Asymmetric Widescreen 16:9 Editorial Bento Grid */}
        {horizontalItems.length > 0 && (
          <motion.div
            layout
            className={`grid grid-cols-1 md:grid-cols-2 ${
              horizontalItems.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
            } gap-x-6 gap-y-10 pt-8`}
          >
            <AnimatePresence>
              {horizontalItems.map((item, idx) => (
                <WidescreenEditorialCard
                  key={item.slug}
                  item={item}
                  index={idx}
                  isFeaturedSpan={idx === 0 && horizontalItems.length >= 4}
                  onSelect={(it) => setTheaterItem(it)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* 2. Dedicated Native 9:16 Vertical Cinema Reel Shelf (Micro-Dramas & Vertical Spots) */}
        {verticalItems.length > 0 && (
          <div className={`${horizontalItems.length > 0 ? 'mt-16 pt-12 border-t border-white/[0.08]' : 'pt-8'}`}>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 sm:gap-4 mb-6">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] mb-1">
                  ● Native 9:16 Vertical Cinema &nbsp;·&nbsp; Hover to Play
                </p>
                <h3 className="text-white text-xl sm:text-2xl font-normal tracking-[-0.025em]">
                  {activeCategory === 'ai_ugc'
                    ? '9:16 Vertical AI UGC & Luxury Product Reels'
                    : 'Commissioned Micro-Dramas & Vertical Brand Reels'}
                </h3>
              </div>
              <span className="text-[12px] font-mono text-white/45">
                {activeCategory === 'ai_ugc'
                  ? 'Fintech Creator · MHPU Handbags · Fine Jewellery D2C'
                  : 'DramaBox · Dashverse · Alcobrew BHRUM'}
              </span>
            </div>

            <div
              className={`grid grid-cols-2 sm:grid-cols-3 ${
                verticalItems.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
              } gap-5`}
            >
              {verticalItems.map((item) => (
                <VerticalReelCard
                  key={item.slug}
                  item={item}
                  onSelect={(it) => setTheaterItem(it)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cinema Lightbox Player */}
      <AnimatePresence>
        {theaterItem && (
          <motion.div
            className="fixed inset-0 z-[130] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setTheaterItem(null)}
          >
            <div
              className="relative w-full max-w-6xl bg-[#0a0a0c] rounded-xl overflow-hidden border border-white/15"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-[#767d88]">
                    {theaterItem.client} &nbsp;·&nbsp; {theaterItem.productionMode}
                  </p>
                  <h3 className="text-lg font-medium tracking-[-0.02em] text-white mt-0.5">
                    {theaterItem.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setTheaterItem(null)}
                  className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer"
                  aria-label="Close player"
                >
                  ✕
                </button>
              </div>

              <div className="aspect-video bg-black flex items-center justify-center">
                <video
                  src={theaterItem.videoUrl}
                  poster={theaterItem.posterUrl}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/10">
                <p className="text-sm text-[#8a909a] max-w-2xl">
                  {theaterItem.description}
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  {theaterItem.watchUrl && (
                    <a
                      href={theaterItem.watchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-full border border-white/20 text-white text-xs hover:bg-white hover:text-black no-underline"
                    >
                      Watch on YouTube ↗
                    </a>
                  )}
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2 rounded-full bg-white text-black text-xs font-medium no-underline"
                  >
                    Book a Call
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
